import type { FC, ReactNode } from 'react'
import type { BrowseFilters, ColorCount } from '@/types'

import Link from 'next/link'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { parseHex } from '@/lib/colors'
import { buildBrowseHref } from '@/lib/browse'

const isLight = (hex: string): boolean => {
    const rgb = parseHex(hex)

    if (!rgb) return false

    const [r, g, b] = rgb

    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62
}

interface ColorTilesProps {
    colors: Array<ColorCount>
}

// Big, tappable color tiles for the home page.
export const ColorTiles: FC<ColorTilesProps> = ({ colors }): ReactNode => {
    return (
        <ul className='grid grid-cols-3 gap-3 sm:grid-cols-5 sm:gap-4 lg:grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))]'>
            {colors.map(color => {
                const light = isLight(color.swatch)

                return (
                    <li key={color.slug}>
                        <Link
                            href={`/wallpapers?color=${color.slug}`}
                            className={cn(
                                'group relative flex aspect-[4/5] flex-col justify-between overflow-hidden rounded-2xl p-3 ring-1 ring-white/10 transition-transform duration-300 hover:-translate-y-1 focus-visible:ring-3 focus-visible:ring-ring/70 focus-visible:outline-none motion-reduce:hover:translate-y-0 sm:p-4',
                                light ? 'text-neutral-900' : 'text-white'
                            )}
                            style={{ backgroundColor: color.swatch }}
                        >
                            <span className={cn('self-end text-xs font-medium', light ? 'text-neutral-900/70' : 'text-white/75')}>
                                {color.count}
                            </span>

                            <span className='text-sm font-semibold sm:text-base'>
                                {color.label}
                            </span>
                        </Link>
                    </li>
                )
            })}
        </ul>
    )
}

interface ColorFilterProps {
    colors: Array<ColorCount>
    filters: BrowseFilters
}

// Compact swatches for the browse filters. Picking the active color again clears it.
export const ColorFilter: FC<ColorFilterProps> = ({ colors, filters }): ReactNode => {
    return (
        <ul className='flex flex-wrap gap-2'>
            {colors.map(color => {
                const active = filters.color === color.slug

                return (
                    <li key={color.slug}>
                        <Link
                            href={buildBrowseHref(filters, { color: active ? '' : color.slug })}
                            scroll={false}
                            aria-current={active ? 'true' : undefined}
                            title={color.label}
                            className={cn(
                                'relative inline-flex size-9 items-center justify-center rounded-full ring-1 transition focus-visible:ring-3 focus-visible:ring-ring/70 focus-visible:outline-none',
                                active ? 'ring-2 ring-foreground ring-offset-2 ring-offset-background' : 'ring-white/15 hover:scale-110 motion-reduce:hover:scale-100'
                            )}
                            style={{ backgroundColor: color.swatch }}
                        >
                            {active && <Check aria-hidden='true' className={cn('size-4', isLight(color.swatch) ? 'text-neutral-900' : 'text-white')} />}
                            <span className='sr-only'>
                                {active ? `Remove the ${color.label.toLowerCase()} filter` : `Show ${color.label.toLowerCase()} wallpapers`}
                            </span>
                        </Link>
                    </li>
                )
            })}
        </ul>
    )
}
